/**
 * Draft rows — the seam between the draft store and the mail list.
 *
 * A draft is NOT a `communications` row. `services/mail_drafts.py` says why: it
 * has no direction, no provider identity, no place in a thread and no delivery,
 * so it lives in its own collection behind its own endpoints. The list renders
 * one anyway — `as_list_entry` shapes a draft like a message — because the
 * Drafts view reuses the inbox's list, rail, paging and row renderer instead of
 * growing a second table.
 *
 * **That reuse is exactly what broke Delete.** A draft row's `comm_id` IS its
 * `draft_id`, so it looks like a message id to every handler that receives it,
 * and every delete path on both lanes targets `communications`:
 *
 *   * **personal** — `POST /mail/move {ids:[draft_id], folder:'trash'}` matched
 *     no row, so `services/mail_state._apply` answered `{matched: 0,
 *     modified: 0}` with a **200**. The surface had already dropped the row
 *     optimistically and toasted "Moved to Trash", so the draft appeared to go
 *     and came back at the next refetch. Reproduced: `set_folder` over a draft
 *     id returns `matched: 0` and the draft is still in the store.
 *   * **unified** — `DELETE /inboxes/{id}/messages/{draft_id}` resolves no
 *     thread rows and answers **404**, so the same click produced an error
 *     toast instead. One defect, two different lies about it.
 *
 * So a draft has to be split out BEFORE either handler runs. `partitionDrafts`
 * is that split, in one module, because the alternative is an `is_draft` branch
 * in six handlers across two surfaces that all have to agree forever.
 */

/**
 * Split rows into the two stores they actually live in.
 *
 * Returns rows, not ids: the personal lane expands a row to its conversation
 * (`thread_comm_ids`) before deleting and needs the whole row to do it, while
 * the draft side needs `draft_id`. Handing back ids would force one caller or
 * the other to re-find the row it just gave away.
 *
 * A mixed selection is ordinary, not exotic: "Drafts" is the only view where
 * drafts appear today, but a selection survives a folder change and the bulk
 * bar acts on whatever is still selected.
 */
export function partitionDrafts(rows) {
    const drafts = [];
    const messages = [];
    for (const row of rows || []) {
        if (!row) continue;
        if (row.is_draft) drafts.push(row);
        else messages.push(row);
    }
    return { drafts, messages };
}

/** The draft ids from a row set, de-duplicated and in list order. */
export function draftIdsOf(rows) {
    return Array.from(
        new Set(
            partitionDrafts(rows)
                .drafts.map((row) => row.draft_id || row.comm_id)
                .filter(Boolean)
        )
    );
}

/**
 * A draft list row, as the composer wants it.
 *
 * `as_list_entry` on the server shapes a draft like a message so the list can
 * render it; this turns it back. The two halves are deliberately adjacent to
 * the field names they translate — a draft row that reopened with the
 * recipients in the wrong field would look like it had lost them.
 *
 * Lives here rather than in `PersonalInboxSurface` (where it started) because
 * the unified lane needs the identical translation: its rows come from the same
 * `as_list_entry`, and a second copy is a second thing to keep in step.
 */
export function draftPayload(row) {
    return {
        draft_id: row.draft_id || row.comm_id,
        to: row.to_emails || [],
        cc: row.cc_emails || [],
        bcc: row.bcc_emails || [],
        subject: row.subject === '(No subject)' ? '' : (row.subject || ''),
        body: row.snippet || '',
        // The refs, not the bytes — they are in S3 already. Omitted here and
        // the draft's attachments were saved and then silently lost on
        // reopen, which is the one failure mode worse than not saving them.
        attachments: row.attachments || [],
    };
}
